/**
 * Session-based voice chat history storage.
 * Namespaced by user + business (V4-LANGUAGE-STORAGE).
 */

import {
  migrateLegacyVoiceStorage,
  readVoiceKey,
  resolveVoiceStorageScope,
  writeVoiceKey,
  type VoiceStorageScope,
} from '../voice/voiceStorage';
import { VOICE_STORAGE_SUFFIXES } from '../voice/constants';

export interface VoiceMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  category?: string;
  card?: VoiceMessageCard;
  isError?: boolean;
}

export interface VoiceMessageCard {
  type: 'invoice' | 'report' | 'expense' | 'confirmation';
  title: string;
  subtitle?: string;
  amount?: string;
  status?: string;
  meta?: Record<string, string>;
}

export interface VoiceSession {
  id: string;
  title: string;
  messages: VoiceMessage[];
  createdAt: string;
  updatedAt: string;
  /** Page where this conversation was started — used for assistant context. */
  origin?: VoiceSessionOrigin;
  /** Ownership stamp — sessions from another scope must not restore. */
  userId?: string;
  businessId?: string;
}

export interface VoiceSessionOrigin {
  pathname: string;
  search: string;
  pageKey: string;
}

const MAX_SESSIONS = 50;
const MAX_MESSAGES_PER_SESSION = 100;

function activeScope(): VoiceStorageScope | null {
  const scope = resolveVoiceStorageScope();
  if (scope) migrateLegacyVoiceStorage(scope);
  return scope;
}

function parseSessions(scope: VoiceStorageScope | null): VoiceSession[] {
  if (!scope) return [];
  try {
    const parsed = readVoiceKey<VoiceSession[]>(scope, VOICE_STORAGE_SUFFIXES.sessions);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSessions(scope: VoiceStorageScope, sessions: VoiceSession[]): void {
  const trimmed = sessions.slice(0, MAX_SESSIONS).map((s) => ({
    ...s,
    userId: s.userId || scope.userId,
    businessId: s.businessId || scope.businessId,
  }));
  writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.sessions, trimmed);
}

function isOwnedSession(
  session: VoiceSession,
  scope: VoiceStorageScope
): boolean {
  if (session.userId && session.userId !== scope.userId) return false;
  if (session.businessId && session.businessId !== scope.businessId) return false;
  return true;
}

export function getCurrentSessionId(): string | null {
  const scope = activeScope();
  if (!scope) return null;
  const id = readVoiceKey<string>(scope, VOICE_STORAGE_SUFFIXES.activeSession);
  return typeof id === 'string' && id ? id : null;
}

export function setCurrentSessionId(id: string): void {
  const scope = activeScope();
  if (!scope) return;
  if (!id) {
    writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.activeSession, '');
    return;
  }
  writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.activeSession, id);
}

export function createSession(
  title = 'New conversation',
  origin?: VoiceSessionOrigin
): VoiceSession {
  const scope = activeScope();
  const now = new Date().toISOString();
  const session: VoiceSession = {
    id: `vs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title,
    messages: [],
    createdAt: now,
    updatedAt: now,
    ...(origin ? { origin } : {}),
    ...(scope
      ? { userId: scope.userId, businessId: scope.businessId }
      : {}),
  };
  if (scope) {
    const sessions = parseSessions(scope);
    sessions.unshift(session);
    saveSessions(scope, sessions);
    setCurrentSessionId(session.id);
  }
  return session;
}

export function getOrCreateCurrentSession(origin?: VoiceSessionOrigin): VoiceSession {
  const scope = activeScope();
  const currentId = getCurrentSessionId();
  const sessions = parseSessions(scope);
  if (currentId && scope) {
    const found = sessions.find((s) => s.id === currentId);
    if (found && isOwnedSession(found, scope)) {
      if (!found.origin && origin) {
        found.origin = origin;
        saveSessions(scope, sessions);
      }
      return found;
    }
  }
  return createSession('New conversation', origin);
}

export function getAllSessions(): VoiceSession[] {
  const scope = activeScope();
  if (!scope) return [];
  return parseSessions(scope).filter((s) => isOwnedSession(s, scope));
}

export function getSessionMessages(sessionId: string): VoiceMessage[] {
  const scope = activeScope();
  const session = parseSessions(scope).find((s) => s.id === sessionId);
  if (!session || (scope && !isOwnedSession(session, scope))) return [];
  return session.messages ?? [];
}

export function updateSessionMessages(
  sessionId: string,
  messages: VoiceMessage[]
): void {
  const scope = activeScope();
  if (!scope) return;
  const sessions = parseSessions(scope);
  const idx = sessions.findIndex((s) => s.id === sessionId);
  if (idx === -1) return;
  if (!isOwnedSession(sessions[idx], scope)) return;

  const trimmed = messages.slice(-MAX_MESSAGES_PER_SESSION);
  const firstUser = trimmed.find((m) => m.role === 'user');
  const title = firstUser
    ? firstUser.content.length > 50
      ? firstUser.content.slice(0, 50) + '…'
      : firstUser.content
    : sessions[idx].title;

  sessions[idx] = {
    ...sessions[idx],
    title,
    messages: trimmed,
    updatedAt: new Date().toISOString(),
    userId: scope.userId,
    businessId: scope.businessId,
  };
  saveSessions(scope, sessions);
}

export function archiveCurrentSession(): void {
  setCurrentSessionId('');
}

export function startNewSession(origin?: VoiceSessionOrigin): VoiceSession {
  archiveCurrentSession();
  return createSession('New conversation', origin);
}

/**
 * Restore a session only when it belongs to the active user/business scope.
 */
export function loadSession(sessionId: string): VoiceSession | null {
  const scope = activeScope();
  if (!scope) return null;
  const session = parseSessions(scope).find((s) => s.id === sessionId);
  if (!session || !isOwnedSession(session, scope)) return null;
  setCurrentSessionId(sessionId);
  return session;
}

export function deleteSession(sessionId: string): void {
  const scope = activeScope();
  if (!scope) return;
  const sessions = parseSessions(scope).filter((s) => s.id !== sessionId);
  saveSessions(scope, sessions);
  if (getCurrentSessionId() === sessionId) {
    archiveCurrentSession();
  }
}

export function clearAllSessions(): void {
  const scope = activeScope();
  if (!scope) return;
  writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.sessions, []);
  writeVoiceKey(scope, VOICE_STORAGE_SUFFIXES.activeSession, '');
}

/** Migrate legacy flat message array to session format (scoped). */
export function migrateLegacyHistory(): void {
  const scope = activeScope();
  if (!scope) return;
  migrateLegacyVoiceStorage(scope);

  const legacy = localStorage.getItem('voiceAssistantHistory');
  if (!legacy || parseSessions(scope).length > 0) return;

  try {
    const messages: VoiceMessage[] = JSON.parse(legacy).map((m: any) => ({
      id: m.id || `legacy_${Date.now()}`,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp || m.savedAt || new Date().toISOString(),
    }));
    if (messages.length === 0) return;

    const session = createSession('Imported conversation');
    updateSessionMessages(session.id, messages);
    localStorage.removeItem('voiceAssistantHistory');
  } catch {
    // ignore
  }
}
